export const CANVAS_STORAGE_KEY = 'lugsail-labs-canvas-v1'
export const CANVAS_RETURN_STORAGE_KEY = 'lugsail-labs-canvas-return-v1'

const CANVAS_DATABASE_NAME = 'lugsail-labs'
const CANVAS_DATABASE_VERSION = 1
const CANVAS_STORE_NAME = 'workspaces'
const INDEXED_DB_SENTINEL = '__indexeddb__'

const CANVAS_RATIOS = {
  '16:9': 16 / 9,
  '4:3': 4 / 3,
  '1:1': 1,
  '4:5': 4 / 5,
  '9:16': 9 / 16,
}

const makeId = (kind) => `${kind}-${Math.random().toString(36).slice(2, 10)}`

function openCanvasDatabase() {
  return new Promise((resolve, reject) => {
    if (!window.indexedDB) {
      reject(new Error('This browser cannot save a large Canvas workspace.'))
      return
    }
    const request = window.indexedDB.open(CANVAS_DATABASE_NAME, CANVAS_DATABASE_VERSION)
    request.onupgradeneeded = () => request.result.createObjectStore(CANVAS_STORE_NAME)
    request.onerror = () => reject(request.error || new Error('Could not open Canvas storage.'))
    request.onsuccess = () => resolve(request.result)
  })
}

function writeCanvasRecord(key, value) {
  return openCanvasDatabase().then((database) => new Promise((resolve, reject) => {
    const transaction = database.transaction(CANVAS_STORE_NAME, 'readwrite')
    transaction.objectStore(CANVAS_STORE_NAME).put(value, key)
    transaction.oncomplete = () => { database.close(); resolve() }
    transaction.onerror = () => { database.close(); reject(transaction.error || new Error('Could not save Canvas workspace.')) }
  }))
}

function readCanvasRecord(key) {
  return openCanvasDatabase().then((database) => new Promise((resolve, reject) => {
    const request = database.transaction(CANVAS_STORE_NAME, 'readonly').objectStore(CANVAS_STORE_NAME).get(key)
    request.onsuccess = () => { database.close(); resolve(request.result || null) }
    request.onerror = () => { database.close(); reject(request.error || new Error('Could not load Canvas workspace.')) }
  }))
}

function readLocalWorkspace() {
  try {
    const saved = localStorage.getItem(CANVAS_STORAGE_KEY)
    return saved && saved !== INDEXED_DB_SENTINEL ? JSON.parse(saved) : null
  } catch {
    return null
  }
}

export function canvasWorkspaceNeedsAsyncLoad() {
  try {
    return localStorage.getItem(CANVAS_STORAGE_KEY) === INDEXED_DB_SENTINEL
  } catch {
    return false
  }
}

export function readCanvasWorkspace() {
  return canvasWorkspaceNeedsAsyncLoad()
    ? readCanvasRecord(CANVAS_STORAGE_KEY)
    : Promise.resolve(readLocalWorkspace())
}

// Most workspaces comfortably fit in localStorage and retain the original
// synchronous startup path. Large datasets transparently move to IndexedDB,
// where the browser gives us substantially more room than the 5 MB web-store
// limit. The small sentinel tells Canvas which store to read after navigation.
export async function saveCanvasWorkspace(workspace) {
  const serialized = JSON.stringify(workspace)
  try {
    localStorage.setItem(CANVAS_STORAGE_KEY, serialized)
    return 'localStorage'
  } catch (error) {
    if (error?.name !== 'QuotaExceededError') throw error
  }

  await writeCanvasRecord(CANVAS_STORAGE_KEY, workspace)
  localStorage.removeItem(CANVAS_STORAGE_KEY)
  localStorage.setItem(CANVAS_STORAGE_KEY, INDEXED_DB_SENTINEL)
  return 'indexedDB'
}

function closestCanvasRatio(visualOptions = {}) {
  const sourceRatio = Number(visualOptions.width) / Number(visualOptions.height)
  if (!Number.isFinite(sourceRatio) || sourceRatio <= 0) return '16:9'

  return Object.entries(CANVAS_RATIOS)
    .sort(([, a], [, b]) => Math.abs(a - sourceRatio) - Math.abs(b - sourceRatio))[0][0]
}

/**
 * Build the smallest Canvas workspace needed to continue editing one existing
 * Lugsail chart. The chart's mapping and visual state are copied verbatim;
 * Canvas ownership begins only after this handoff.
 */
export function createCanvasWorkspaceFromProduction({
  chart,
  dataset,
  dataTypes,
  mapping,
  visualOptions,
  brandTheme,
  datasetName,
}) {
  if (!chart?.metadata?.id || !Array.isArray(dataset) || !dataTypes) return null

  const datasetId = makeId('dataset')
  const aspectRatio = closestCanvasRatio(visualOptions)
  const chartWidth = 500

  return {
    dataset: {
      id: datasetId,
      name: datasetName || 'Current Lugsail dataset',
      rows: dataset,
      dataTypes,
      // Production data has already passed through Lugsail's parser.
      isParsed: true,
    },
    objects: [
      { id: makeId('dataset'), kind: 'dataset', x: 120, y: 180, w: 260, h: 236 },
      {
        id: makeId('chart'),
        kind: 'chart',
        title: brandTheme?.title || chart.metadata.name,
        x: 480,
        y: 120,
        w: chartWidth,
        h: chartWidth / CANVAS_RATIOS[aspectRatio],
        datasetId,
        chartId: chart.metadata.id,
        mapping: mapping || {},
        visualOptions: visualOptions || {},
        brandTheme,
        aspectRatio,
      },
    ],
    camera: { x: 0, y: 0, zoom: 1 },
  }
}

export async function openProductionChartInCanvas(snapshot) {
  const workspace = createCanvasWorkspaceFromProduction(snapshot)
  if (!workspace) return false
  await saveCanvasWorkspace(workspace)
  window.location.assign('/labs/canvas')
  return true
}

export function hasCanvasWorkspace() {
  if (canvasWorkspaceNeedsAsyncLoad()) return true
  try {
    return Boolean(readLocalWorkspace()?.objects?.length)
  } catch {
    return false
  }
}

/**
 * Canvas owns position and iteration history, while Lugsail owns the editing
 * workflow. This handoff intentionally contains only the existing Lugsail
 * chart contract, so the normal project importer remains the single source of
 * truth for loading data, mapping, styling, and rendering.
 */
export function createProductionProjectFromCanvas({ chart, dataset, spec }) {
  if (!chart || !dataset?.rows?.length || !dataset.dataTypes || !spec) return null
  return {
    userInput: '',
    userData: dataset.rows,
    userDataType: 'canvas',
    parseError: null,
    unstackedData: null,
    unstackedColumns: null,
    dataTypes: dataset.dataTypes,
    separator: ',',
    thousandsSeparator: ',',
    decimalsSeparator: '.',
    locale: navigator.language || 'en-US',
    stackDimension: null,
    dataSource: { type: 'canvas', datasetTitle: dataset.name || 'Canvas dataset' },
    // Charts contain renderer functions, so only their stable Lugsail ID is
    // persisted. AppShell resolves it back to the registered chart instance.
    canvasChartId: chart.metadata.id,
    mapping: spec.mapping || {},
    visualOptions: spec.visualOptions || {},
    brandTheme: spec.brandTheme,
  }
}

export function openCanvasChartInLugsail(snapshot) {
  const project = createProductionProjectFromCanvas(snapshot)
  if (!project) return false
  localStorage.setItem(CANVAS_RETURN_STORAGE_KEY, JSON.stringify(project))
  window.location.assign('/')
  return true
}

export function readCanvasReturnProject() {
  try {
    return JSON.parse(localStorage.getItem(CANVAS_RETURN_STORAGE_KEY))
  } catch {
    return null
  }
}

export function clearCanvasReturnProject() {
  localStorage.removeItem(CANVAS_RETURN_STORAGE_KEY)
}
